/**
 * Strap Bundle Upsell - Context-Aware Version
 * Modes: bundle (both), strap_only, pad_only
 * 20251120 1140 
 */

class StrapBundleUpsellBlock {
  constructor(blockId) {
    this.blockId = blockId;
    this.block = document.querySelector(`.strap-bundle-upsell-block[data-block-id="${blockId}"]`);
    
    if (!this.block) {
      console.error(`Block not found: ${blockId}`);
      return;
    }

    // Detect mode from block
    this.mode = this.block.dataset.mode || 'bundle';
    this.showStrap = this.block.dataset.showStrap === 'true';
    this.showPad = this.block.dataset.showPad === 'true';

    // Get elements
    this.strapCheckbox = this.block.querySelector(`[data-upsell-strap="${blockId}"]`);
    this.padCheckbox = this.block.querySelector(`[data-upsell-pad="${blockId}"]`);
    this.strapColors = this.block.querySelector(`[data-strap-colors="${blockId}"]`);
    this.padColors = this.block.querySelector(`[data-pad-colors="${blockId}"]`);
    this.addButton = this.block.querySelector(`[data-upsell-add="${blockId}"]`);
    this.messageContainer = this.block.querySelector(`[data-upsell-message="${blockId}"]`);
    this.savingsMessage = this.block.querySelector(`[data-bundle-savings="${blockId}"]`);
    this.totalPriceSpan = this.block.querySelector(`[data-total-price="${blockId}"]`);
    this.padPriceSpan = this.block.querySelector(`[data-pad-price="${blockId}"]`);

    // Validate required elements based on mode
    const hasRequired = this.validateElements();
    if (!hasRequired) {
      console.error(`${this.mode} mode: Required elements not found for block ${blockId}`);
      return;
    }

    this.selectedStrapVariant = null;
    this.selectedPadVariant = null;

    console.log(`✓ Bundle block initialized in ${this.mode} mode`);
    this.init();
  }

  validateElements() {
    // In single-item modes, only one checkbox is required
    if (this.mode === 'strap_only') {
      return this.strapCheckbox && this.addButton;
    } else if (this.mode === 'pad_only') {
      return this.padCheckbox && this.addButton;
    } else {
      // Bundle mode needs both
      return this.strapCheckbox && this.padCheckbox && this.addButton;
    }
  }

  init() {
    // Set up event listeners based on what's visible
    if (this.strapCheckbox) {
      this.strapCheckbox.addEventListener('change', () => this.handleStrapToggle());
    }

    if (this.padCheckbox) {
      this.padCheckbox.addEventListener('change', () => this.handlePadToggle());
    }

    // Color swatch listeners
    if (this.strapColors) {
      this.strapColors.querySelectorAll('.color-swatch').forEach(swatch => {
        swatch.addEventListener('click', (e) => this.handleColorSelect(e, 'strap'));
      });
    }

    if (this.padColors) {
      this.padColors.querySelectorAll('.color-swatch').forEach(swatch => {
        swatch.addEventListener('click', (e) => this.handleColorSelect(e, 'pad'));
      });
    }

    // Add button listener
    this.addButton.addEventListener('click', () => this.addToCart());

    // In single-item modes, auto-select first color and show swatches
    if (this.mode === 'strap_only' && this.strapColors) {
      const firstSwatch = this.strapColors.querySelector('.color-swatch');
      if (firstSwatch) firstSwatch.click();
    } else if (this.mode === 'pad_only' && this.padColors) {
      const firstSwatch = this.padColors.querySelector('.color-swatch');
      if (firstSwatch) firstSwatch.click();
    }

    this.updateUI();
  }

  handleStrapToggle() {
    if (this.strapCheckbox.checked) {
      if (this.strapColors) {
        this.strapColors.style.display = 'block';
        if (!this.selectedStrapVariant) {
          const firstSwatch = this.strapColors.querySelector('.color-swatch');
          if (firstSwatch) firstSwatch.click();
        }
      }
    } else {
      if (this.strapColors) this.strapColors.style.display = 'none';
      this.selectedStrapVariant = null;
    }
    this.updateUI();
  }

  handlePadToggle() {
    if (this.padCheckbox.checked) {
      if (this.padColors) {
        this.padColors.style.display = 'block';
        if (!this.selectedPadVariant) {
          const firstSwatch = this.padColors.querySelector('.color-swatch');
          if (firstSwatch) firstSwatch.click();
        }
      }
    } else {
      if (this.padColors) this.padColors.style.display = 'none';
      this.selectedPadVariant = null;
    }
    this.updateUI();
  }

  handleColorSelect(event, type) {
    const swatch = event.currentTarget;
    const variantId = swatch.dataset.variant;
    const container = type === 'strap' ? this.strapColors : this.padColors;

    container.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('selected'));
    swatch.classList.add('selected');

    if (type === 'strap') {
      this.selectedStrapVariant = variantId;
    } else {
      this.selectedPadVariant = variantId;
    }

    this.updateUI();
  }

  updateUI() {
    const hasStrap = this.strapCheckbox && this.strapCheckbox.checked && this.selectedStrapVariant;
    const hasPad = this.padCheckbox && this.padCheckbox.checked && this.selectedPadVariant;
    const hasAny = hasStrap || hasPad;
    const isBoth = hasStrap && hasPad;

    // In single-item modes, button is always visible if color selected
    if (this.mode === 'strap_only') {
      this.addButton.style.display = this.selectedStrapVariant ? 'block' : 'none';
    } else if (this.mode === 'pad_only') {
      this.addButton.style.display = this.selectedPadVariant ? 'block' : 'none';
    } else {
      // Bundle mode: show button if anything selected
      this.addButton.style.display = hasAny ? 'block' : 'none';
    }

    // Update pad price for bundle discount (only in bundle mode)
    if (this.mode === 'bundle' && this.padPriceSpan) {
      if (isBoth) {
        const comboPrice = this.padCheckbox.dataset.priceCombo;
        this.padPriceSpan.textContent = `$${comboPrice}`;
        this.padPriceSpan.style.color = '#d12b2b';
      } else {
        const regularPrice = this.padCheckbox.dataset.priceRegular;
        this.padPriceSpan.textContent = `$${regularPrice}`;
        this.padPriceSpan.style.color = '#d12b2b';
      }
    }

    // Show/hide bundle savings (only in bundle mode)
    if (this.mode === 'bundle' && this.savingsMessage) {
      this.savingsMessage.style.display = isBoth ? 'block' : 'none';
    }

    // Calculate total
    if (this.totalPriceSpan) {
      let total = 0;
      
      if (hasStrap && this.strapCheckbox) {
        total += parseFloat(this.strapCheckbox.dataset.price);
      }
      
      if (hasPad && this.padCheckbox) {
        const price = (this.mode === 'bundle' && isBoth) ? 
          parseFloat(this.padCheckbox.dataset.priceCombo) : 
          parseFloat(this.padCheckbox.dataset.priceRegular);
        total += price;
      }

      this.totalPriceSpan.textContent = total.toFixed(2);
    }

    // Disable button if required selections not made
    if (this.mode === 'strap_only') {
      this.addButton.disabled = !this.selectedStrapVariant;
    } else if (this.mode === 'pad_only') {
      this.addButton.disabled = !this.selectedPadVariant;
    } else {
      this.addButton.disabled = !hasAny || 
        (this.strapCheckbox.checked && !this.selectedStrapVariant) ||
        (this.padCheckbox.checked && !this.selectedPadVariant);
    }
  }

  showMessage(message, type = 'success') {
    this.messageContainer.textContent = message;
    this.messageContainer.className = `upsell-message ${type}`;
    this.messageContainer.style.display = 'block';

    if (type === 'success') {
      setTimeout(() => {
        this.messageContainer.style.display = 'none';
      }, 3000);
    }
  }

  setButtonLoading(isLoading) {
    const buttonText = this.addButton.querySelector('.button-text');
    const buttonLoading = this.addButton.querySelector('.button-loading');

    if (isLoading) {
      buttonText.style.display = 'none';
      buttonLoading.style.display = 'inline';
      this.addButton.disabled = true;
    } else {
      buttonText.style.display = 'inline';
      buttonLoading.style.display = 'none';
      this.updateUI();
    }
  }

  confettiBurst() {
    const rect = this.addButton.getBoundingClientRect();
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + window.scrollY;
    const N = 18;
    
    for(let i = 0; i < N; i++) {
      const p = document.createElement('i');
      p.className = 'si-confetti';
      p.style.left = originX + 'px';
      p.style.top = originY + 'px';
      p.style.setProperty('--dx', (Math.random() * 2 - 1) * 120 + 'px');
      p.style.setProperty('--dy', (Math.random() * -1 - 0.2) * 180 + 'px');
      p.style.setProperty('--rot', (Math.random() * 360) + 'deg');
      document.body.appendChild(p);
      requestAnimationFrame(() => p.classList.add('in'));
      setTimeout(() => p.remove(), 900);
    }
  }

  async addToCart() {
    const items = [];

    // Add items based on what's checked and selected
    if (this.strapCheckbox && this.strapCheckbox.checked && this.selectedStrapVariant) {
      items.push({ id: this.selectedStrapVariant, quantity: 1 });
    }

    if (this.padCheckbox && this.padCheckbox.checked && this.selectedPadVariant) {
      items.push({ id: this.selectedPadVariant, quantity: 1 });
    }

    if (items.length === 0) {
      this.showMessage('Please select a color', 'error');
      return;
    }

    this.setButtonLoading(true);
    this.messageContainer.style.display = 'none';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

      const result = await response.json();
      console.log('✓ Added to cart:', result);

      // Confetti if enabled
      if (window.SI_ENABLE_CONFETTI !== false) {
        this.confettiBurst();
      }

      // Success message based on mode
      let message = '✓ Added to cart!';
      if (this.mode === 'bundle' && items.length === 2) {
        message = '✓ Bundle added!';
      }
      
      this.showMessage(message, 'success');

      // Redirect to cart after brief moment for visual feedback
      setTimeout(() => {
        window.location.href = '/cart';
      }, 500);

    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      
      let errorMessage = 'Sorry, there was an error. Please try again.';
      
      if (error.message.includes('sold out') || error.message.includes('available')) {
        errorMessage = 'Selected item(s) are currently out of stock.';
      }
      
      this.showMessage(errorMessage, 'error');
      this.setButtonLoading(false);
    }
  }
}

// Initialize all block instances
function initStrapBundleUpsellBlocks() {
  document.querySelectorAll('.strap-bundle-upsell-block[data-block-id]').forEach(block => {
    const blockId = block.dataset.blockId;
    if (blockId) {
      new StrapBundleUpsellBlock(blockId);
    }
  });
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStrapBundleUpsellBlocks);
} else {
  initStrapBundleUpsellBlocks();
}

// Also re-initialize when Shopify theme editor loads sections
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initStrapBundleUpsellBlocks);
}